import React, { useRef, useState } from "react"
import PillEditor from "./PillEditor"
import { ActionIcon, RemoveButton, area, field, label } from "./styles"

const RECIPIENT_TYPES = ["notify", "email", "subscribe"]

const Recipients = ({ action, objectRoles, roles, users, update }) => {
  const [query, setQuery] = useState("")
  const offered = roles.filter(role => objectRoles.includes(role.value) && !(role.value === "followers" && action.type === "subscribe"))
  const toggle  = (list, value) => (list.includes(value) ? list.filter(v => v !== value) : [...list, value])
  const shown   = users.filter(user => !query || user.name.toLowerCase().includes(query.trim().toLowerCase()))

  return (
    <>
      {offered.length > 0 && (
        <div>
          <span className={label}>{I18n.t("automations.form.who")}</span>
          <div className="flex flex-wrap gap-1.5">
            {offered.map(role => (
              <label key={role.value}
                     className="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-slate-200 !px-2.5 !py-1 text-[12px] text-slate-600 has-[:checked]:border-blue-300 has-[:checked]:bg-blue-50 has-[:checked]:text-blue-700">
                <input type="checkbox" className="h-3.5 w-3.5 accent-blue-600" checked={action.roles.includes(role.value)}
                       onChange={() => update({ roles: toggle(action.roles, role.value) })} />
                {role.label}
              </label>
            ))}
          </div>
        </div>
      )}

      <div>
        <span className={label}>{I18n.t("automations.form.users")}</span>
        <div className="rounded-lg border border-slate-200">
          <input type="search" value={query} onChange={e => setQuery(e.target.value)} placeholder={I18n.t("automations.form.search_users")}
                 className="w-full rounded-t-lg border-0 border-b border-slate-100 !px-3 !py-1.5 text-[12.5px] focus:outline-none" />
          <div className="max-h-36 overflow-y-auto !p-1.5">
            {shown.map(user => (
              <label key={user.id} className="flex cursor-pointer items-center gap-2 rounded-md !px-2 !py-1 text-[12.5px] text-slate-700 hover:bg-slate-50">
                <input type="checkbox" className="h-3.5 w-3.5 accent-blue-600" checked={action.userIds.includes(user.id)}
                       onChange={() => update({ userIds: toggle(action.userIds, user.id) })} />
                {user.name}
              </label>
            ))}
          </div>
        </div>
      </div>
    </>
  )
}

const EmailFields = ({ action, object, variables, update }) => {
  const [panel, setPanel] = useState(false)
  const active  = useRef("body")
  const subject = useRef(null)
  const body    = useRef(null)
  const groups  = variables.groups.filter(group => group.kinds.includes(object))

  const insert = token => {
    if (active.current === "body") return body.current.insert(token)

    const input = subject.current
    const start = input.selectionStart ?? input.value.length
    const end   = input.selectionEnd ?? input.value.length
    const value = input.value.slice(0, start) + token + input.value.slice(end)
    update({ subject: value })
    requestAnimationFrame(() => { input.focus(); input.setSelectionRange(start + token.length, start + token.length) })
  }

  const leave = event => { if (!event.currentTarget.contains(event.relatedTarget)) setPanel(false) }

  return (
    <div className="tw-interp space-y-3" onFocus={() => setPanel(true)} onBlur={leave}>
      <div>
        <span className={label}>{I18n.t("automations.form.subject")}</span>
        <input ref={subject} type="text" className={field} value={action.subject}
               onChange={e => update({ subject: e.target.value })} onFocus={() => { active.current = "subject" }} />
      </div>
      <div>
        <span className={label}>{I18n.t("automations.form.body")}</span>
        <PillEditor ref={body} initialValue={action.body} labels={variables.labels} removeLabel={I18n.t("button.remove")}
                    className={`${area} min-h-[120px] leading-relaxed`} onChange={value => update({ body: value })}
                    onFocus={() => { active.current = "body" }} />
      </div>
      {panel && groups.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 !p-2.5" onMouseDown={e => e.preventDefault()}>
          <p className="!mb-1.5 text-[11px] text-slate-500"><i className="fas fa-magic !mr-1 text-blue-500" />{I18n.t("automations.form.variables_hint")}</p>
          {groups.map(group => (
            <div key={group.root} className="flex flex-wrap items-center gap-1 !py-0.5">
              <span className="w-[110px] shrink-0 text-[11px] font-semibold text-slate-500">{group.label}</span>
              {group.variables.map(variable => (
                <button key={variable.token} type="button" onClick={() => insert(variable.token)}
                        className="rounded-full border border-slate-200 bg-white !px-2 !py-0.5 text-[11.5px] text-slate-600 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                  {variable.label}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

const ActionCard = ({ action, type, object, objectRoles, props, update, remove }) => (
  <div className="rounded-xl border border-slate-200 bg-white">
    <div className="flex items-center gap-3 border-b border-slate-100 !px-3.5 !py-2.5">
      <ActionIcon type={type} />
      <span className="flex-1 text-[13px] font-semibold text-slate-800">{type.label}</span>
      <RemoveButton onClick={remove} />
    </div>

    <div className="space-y-3 !p-3.5">
      {RECIPIENT_TYPES.includes(action.type) && (
        <Recipients action={action} objectRoles={objectRoles} roles={props.roles} users={props.users} update={update} />
      )}

      {action.type === "notify" && (
        <div>
          <span className={label}>{I18n.t("automations.form.message")}</span>
          <input type="text" className={field} value={action.message} placeholder={I18n.t("automations.form.message_placeholder")}
                 onChange={e => update({ message: e.target.value })} />
        </div>
      )}

      {action.type === "email" && (
        <>
          <div>
            <span className={label}>{I18n.t("automations.form.emails")}</span>
            <input type="text" className={field} value={action.emails} placeholder="compta@exemple.fr, direction@exemple.fr"
                   onChange={e => update({ emails: e.target.value })} />
          </div>
          <EmailFields action={action} object={object} variables={props.variables} update={update} />
        </>
      )}

      {action.type === "create_activity" && (
        <div className="grid gap-3 sm:grid-cols-[160px_minmax(0,1fr)_110px]">
          <div>
            <span className={label}>{I18n.t("automations.form.activity_type")}</span>
            <select className={field} value={action.activityType} onChange={e => update({ activityType: e.target.value })}>
              {props.activityTypes.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <span className={label}>{I18n.t("automations.form.subject")}</span>
            <input type="text" className={field} value={action.subject} placeholder={I18n.t("automations.default_activity_subject")}
                   onChange={e => update({ subject: e.target.value })} />
          </div>
          <div>
            <span className={label}>{I18n.t("automations.form.due_in_days")}</span>
            <input type="number" min="0" className={field} value={action.dueInDays} placeholder="1"
                   onChange={e => update({ dueInDays: e.target.value })} />
          </div>
        </div>
      )}

      {action.type === "set_priority" && (
        <div className="max-w-[220px]">
          <span className={label}>{I18n.t("automations.form.priority")}</span>
          <select className={field} value={action.priority} onChange={e => update({ priority: e.target.value })}>
            {props.priorities.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      )}

      {action.type === "add_note" && (
        <div>
          <span className={label}>{I18n.t("automations.form.note")}</span>
          <textarea rows={3} className={area} value={action.body} onChange={e => update({ body: e.target.value })} />
        </div>
      )}
    </div>
  </div>
)

export default ActionCard
